import { supabase } from "@/integrations/supabase/client";
import { documentWriteError, PinnedDocumentError } from "./pinnedDocuments";
import type { Document } from "@/types/documents";
import { fromDocumentDB } from "@/types/documents";
import {
  documentEventRows,
  logDocumentsDeleted,
  logDocumentsRestored,
  restoreFolderAncestorsForDocs,
  type DocumentEventRow,
} from "@/stores/documents/repository";
import type { SortKey, SortOrder } from "../utils/documentSorting";
import {
  invalidateBuildingStorageQuota,
  storageAccessErrorFrom,
  storageQuotaErrorFrom,
} from "./buildingStorageQuota";

export type DocumentCategory = "documents" | "drawings" | "unsorted" | "trash";

export interface DocumentFilters {
  searchQuery: string;
  searchScope: "folder" | "global";
  sortKey: SortKey;
  sortOrder: SortOrder;
}

/** A version-group row carrying the columns an audit event is built from. */
interface VersionRow {
  id: number;
  filename: string;
  building_id: number | null;
  org_id: string | null;
  is_latest_version: boolean | null;
  hidden: boolean | null;
  is_dwg_child: boolean | null;
}

/** Is this a row a document list would show? Matches the history's own reads. */
function isUserVisibleRow(row: VersionRow): boolean {
  return row.is_latest_version === true && !row.hidden && !row.is_dwg_child;
}

function toDocumentEventRow(row: VersionRow): DocumentEventRow {
  return {
    id: row.id,
    filename: row.filename,
    building_id: row.building_id,
    org_id: row.org_id,
  };
}

/** Join first/last name from an embedded user_profiles row, or undefined. */
function profileName(
  profile: { first_name?: string | null; last_name?: string | null } | null
): string | undefined {
  if (!profile) return undefined;
  return [profile.first_name, profile.last_name].filter(Boolean).join(" ") || undefined;
}

export async function getDocuments(
  buildingId: string,
  category?: DocumentCategory,
  filters?: DocumentFilters
): Promise<Document[]> {
  let query = supabase
    .from("documents")
    .select(
      "*, user_profiles!documents_created_by_fk(id,first_name,last_name,avatar_url,color), document_document_categories(document_categories(*)), document_design_disciplines(design_disciplines(id, name, name_en, abbreviation, description)), document_design_stages(design_stages(id, name, name_en, abbreviation))"
    )
    .eq("building_id", parseInt(buildingId, 10))
    .eq("is_deleted", false); // Exclude deleted documents

  console.log(
    "📄 [documentService] Fetching documents with categories from M2M table for building:",
    buildingId
  );

  // Note: category field no longer exists in database schema
  // Documents are now organized by folders instead

  // Only apply search query filter here, other filters are handled client-side
  if (filters?.searchQuery) {
    query = query.ilike("filename", `%${filters.searchQuery}%`);
  }

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }

  return (data || []).map((row) => {
    const doc = fromDocumentDB(row as any);
    return {
      ...doc,
      created_by_name: profileName(row.user_profiles),
      created_by_avatar_url: row.user_profiles?.avatar_url ?? null,
      created_by_color: row.user_profiles?.color ?? null,
    } as Document;
  });
}

/**
 * Hide cascade-trashed DWG children in the trash listing: when a DWG is
 * trashed its converted PDF goes with it (DB trigger cascades via
 * parent_document_id), but showing both rows reads as two documents. The
 * DWG row represents the pair — restore and permanent delete cascade to
 * the child in the DB. A child whose parent is NOT in the trash (edge
 * case) stays visible so it can still be managed.
 */
export function hideCascadeTrashedChildren(docs: Document[]): Document[] {
  const trashedIds = new Set(docs.map((d) => String(d.id)));
  return docs.filter(
    (d) => !(d.parent_document_id && trashedIds.has(String(d.parent_document_id)))
  );
}

export async function getTrashedDocuments(
  buildingId: string,
  projectId?: string | null
): Promise<Document[]> {
  let query = supabase
    .from("documents")
    .select(
      "*, user_profiles!documents_created_by_fk(id,first_name,last_name,avatar_url,color), deleted_by_profile:user_profiles!documents_deleted_by_fk(id,first_name,last_name,avatar_url), document_document_categories(document_categories(*)), document_design_disciplines(design_disciplines(id, name, name_en, abbreviation, description)), document_design_stages(design_stages(id, name, name_en, abbreviation))"
    )
    .eq("building_id", parseInt(buildingId, 10))
    .eq("is_deleted", true)
    .order("deleted_at", { ascending: false });
  if (projectId) query = query.eq("project_id", Number(projectId));
  const { data, error } = await query;

  console.log(
    "🗑️ [documentService] Fetching trashed documents with categories from M2M table for building:",
    buildingId
  );

  if (error) {
    console.error("Error fetching trashed documents:", error);
    throw error;
  }

  const docs = (data || []).map((row) => {
    const doc = fromDocumentDB(row as any);
    return {
      ...doc,
      created_by_name: profileName(row.user_profiles),
      created_by_avatar_url: row.user_profiles?.avatar_url ?? null,
      created_by_color: row.user_profiles?.color ?? null,
      deleted_by_name: profileName(row.deleted_by_profile),
    } as Document;
  });

  return hideCascadeTrashedChildren(docs);
}

export async function downloadDocument(document: Document): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from("documents")
      .createSignedUrl(document.file_path, 60);

    if (error) throw error;
    return data.signedUrl;
  } catch (error) {
    console.error("Download error:", error);
    return null;
  }
}

export async function moveToTrash(document: Document, userId: number): Promise<boolean> {
  try {
    let query = supabase
      .from("documents")
      .select("id, filename, building_id, org_id, is_latest_version, hidden, is_dwg_child")
      .eq("building_id", Number(document.building_id))
      .eq("is_deleted", false);
    query = document.version_group
      ? query.eq("version_group", document.version_group)
      : query.eq("id", Number(document.id));
    const { data: versions, error: fetchError } = await query;
    if (fetchError) throw fetchError;

    if (versions && versions.length > 0) {
      const versionIds = versions.map((version) => version.id);
      const { error } = await supabase
        .from("documents")
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_by: userId,
        })
        .in("id", versionIds);
      if (error) throw documentWriteError(error);
      await logDocumentsDeleted(versions.filter(isUserVisibleRow).map(toDocumentEventRow));
      invalidateBuildingStorageQuota(document.building_id);
    }

    return true;
  } catch (error) {
    // A pinned file is a refusal to explain, not a failure: the caller says so.
    if (error instanceof PinnedDocumentError) throw error;
    console.error("Move to trash error:", error);
    return false;
  }
}

function restoreStorageError(error: unknown): Error | null {
  if (!error || typeof error !== "object" || !("message" in error)) return null;
  const response = error as { code?: string; message?: string };
  return storageQuotaErrorFrom(response) ?? storageAccessErrorFrom(response);
}

async function restoreDocumentVersionRows(
  versionIds: number[],
  buildingId: Document["building_id"]
): Promise<void> {
  const { error } = await supabase
    .from("documents")
    .update({ is_deleted: false, deleted_at: null, deleted_by: null })
    .in("id", versionIds);
  if (error) throw error;
  await logDocumentsRestored(await documentEventRows(versionIds, { visibleOnly: true }));
  await restoreFolderAncestorsForDocs(versionIds);
  invalidateBuildingStorageQuota(buildingId);
}

export async function restoreDocument(document: Document): Promise<boolean> {
  try {
    let query = supabase
      .from("documents")
      .select("id, filename")
      .eq("building_id", Number(document.building_id))
      .eq("is_deleted", true);
    query = document.version_group
      ? query.eq("version_group", document.version_group)
      : query.eq("id", Number(document.id));
    const { data: versions, error: fetchError } = await query;
    if (fetchError) throw fetchError;

    if (versions && versions.length > 0) {
      await restoreDocumentVersionRows(
        versions.map((version) => version.id),
        document.building_id
      );
    }

    return true;
  } catch (error) {
    const quotaError = restoreStorageError(error);
    if (quotaError) throw quotaError;
    console.error("Restore document error:", error);
    return false;
  }
}

export async function emptyTrash(buildingId: string): Promise<{ success: boolean; count: number }> {
  try {
    // Get all trashed documents for this building
    const { data: trashedDocs, error: fetchError } = await supabase
      .from("documents")
      .select("id")
      .eq("building_id", parseInt(buildingId, 10))
      .eq("is_deleted", true);

    if (fetchError) throw fetchError;

    const count = trashedDocs?.length || 0;

    if (count === 0) {
      return { success: true, count: 0 };
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from("documents")
      .delete()
      .eq("building_id", parseInt(buildingId, 10))
      .eq("is_deleted", true);

    if (dbError) throw dbError;

    // Storage objects are intentionally kept — documents-bucket objects are
    // never hard-deleted (copies share file_path with their source, and
    // binaries must stay recoverable). Deleted rows are snapshotted into
    // deleted_documents_archive by the trg_archive_deleted_document trigger
    // (franklin-data-pipeline migration
    // 20260721110259_prevent_storage_hard_delete.sql).
    return { success: true, count };
  } catch (error) {
    console.error("Empty trash error:", error);
    return { success: false, count: 0 };
  }
}

// Purge documents that have been in trash for more than 30 days
export async function purgeExpiredTrashedDocuments(
  buildingId: string
): Promise<{ success: boolean; count: number }> {
  try {
    const threshold = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

    // Find expired trashed documents
    const { data: expiredDocs, error: fetchError } = await supabase
      .from("documents")
      .select("id")
      .eq("building_id", parseInt(buildingId, 10))
      .eq("is_deleted", true)
      .lt("deleted_at", threshold);

    if (fetchError) throw fetchError;

    const count = expiredDocs?.length || 0;
    if (!count) return { success: true, count: 0 };

    const ids = expiredDocs.map((d) => d.id);

    // Delete rows
    const { error: dbError } = await supabase.from("documents").delete().in("id", ids);
    if (dbError) throw dbError;

    // Storage objects are intentionally kept — see emptyTrash.
    return { success: true, count };
  } catch (error) {
    console.error("Purge expired trash error:", error);
    return { success: false, count: 0 };
  }
}

export async function permanentlyDeleteDocument(document: Document): Promise<boolean> {
  try {
    console.log("🗑️ Permanently deleting document:", document.filename);

    // Extract base filename to find all versions (same logic as delete/restore)
    const filenameParts = document.filename.split(".");
    const extension = filenameParts.length > 1 ? `.${filenameParts.pop()}` : "";
    const nameWithoutExt = filenameParts.join(".");

    // Create a base name by removing version patterns (same logic as UI)
    const baseName = nameWithoutExt
      .replace(/[-_\s]v\d+$/i, "") // Remove patterns like "-v1", "_v2", " v3"
      .replace(/[-_\s]\d+$/i, "") // Remove patterns like "-1", "_2", " 3"
      .replace(/\s+\(\d+\)$/i, ""); // Remove patterns like " (1)", " (2)"

    console.log(
      "📦 Searching for all versions to permanently delete with base name:",
      baseName + extension
    );

    // First, get all trashed documents that might be versions of this file
    const { data: allVersions, error: fetchError } = await supabase
      .from("documents")
      .select("id, filename, file_path")
      .eq("building_id", parseInt(String(document.building_id), 10))
      .eq("is_deleted", true);

    if (fetchError) throw fetchError;

    // Filter documents that match our version patterns
    const versionsToPermanentlyDelete =
      allVersions?.filter((doc) => {
        const docParts = doc.filename.split(".");
        const docExt = docParts.length > 1 ? `.${docParts.pop()}` : "";
        const docNameWithoutExt = docParts.join(".");

        const docBaseName = docNameWithoutExt
          .replace(/[-_\s]v\d+$/i, "")
          .replace(/[-_\s]\d+$/i, "")
          .replace(/\s+\(\d+\)$/i, "");

        // Check if this document belongs to the same version group
        return docBaseName + docExt === baseName + extension;
      }) || [];

    console.log(
      `🗂️ Found ${versionsToPermanentlyDelete.length} version(s) to permanently delete:`,
      versionsToPermanentlyDelete.map((d) => d.filename)
    );

    if (versionsToPermanentlyDelete.length > 0) {
      const versionIds = versionsToPermanentlyDelete.map((v) => v.id);

      // Delete all versions from database
      const { error: dbError } = await supabase.from("documents").delete().in("id", versionIds);

      if (dbError) throw dbError;

      // Storage objects are intentionally kept — see emptyTrash.
    }

    return true;
  } catch (error) {
    console.error("Permanent delete error:", error);
    return false;
  }
}

// Service object for backward compatibility
export const documentService = {
  fetchDocuments: getDocuments,
  fetchTrashedDocuments: getTrashedDocuments,
  download: downloadDocument,
  moveToTrash,
  restore: restoreDocument,
  permanentDelete: permanentlyDeleteDocument,
  emptyTrash,
  purgeExpiredTrashedDocuments,
};

export function mapUiCategoryToDbCategory(
  category: "drawings" | "documents" | "unsorted" | "trash"
): DocumentCategory {
  return category;
}

export async function autoSortDocuments(
  buildingId: string
): Promise<{ success: boolean; count: number }> {
  // Placeholder implementation
  console.log("Auto-sorting documents for building:", buildingId);
  return { success: true, count: 0 };
}
